<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <span class="mb-1 font-medium">סוג השובר</span>
    <div class="flex gap-2">
      <button
        type="button"
        class="flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors"
        :class="form.voucherType === VoucherTypes.AMOUNT
          ? 'border-primary bg-primary-50 text-primary'
          : 'border-gray-200 bg-white text-gray-600'"
        @click="form.voucherType = VoucherTypes.AMOUNT"
      >₪ סכום</button>
      <button
        type="button"
        class="flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors"
        :class="form.voucherType === VoucherTypes.SERVICE
          ? 'border-primary bg-primary-50 text-primary'
          : 'border-gray-200 bg-white text-gray-600'"
        @click="form.voucherType = VoucherTypes.SERVICE"
      >שירות</button>
    </div>

    <template v-if="form.voucherType === VoucherTypes.AMOUNT">
      <span class="mt-2 mb-1 font-medium">סכום השובר</span>
      <input-number
        v-model="form.amount"
        :min="1"
        :maxFractionDigits="0"
        mode="currency"
        currency="ILS"
        locale="he-IL"
        placeholder="למשל: 100"
      />
    </template>

    <template v-else>
      <span class="mt-2 mb-1 font-medium">שירותים הכלולים בשובר</span>
      <select-service
        v-model="selectedServices"
        @services-selected="onServicesSelected"
      />
      <span class="text-xs text-gray-500">
        השובר יכסה את מלוא עלות השירותים. הקונה ישלם את הסכום המלא.
      </span>

      <div v-if="serviceTotalPrice > 0" class="mt-2 flex items-baseline gap-2">
        <span class="font-medium">סה״כ לרכישה:</span>
        <span class="text-lg font-semibold text-primary">₪{{ serviceTotalPrice }}</span>
      </div>
    </template>

    <span class="mt-2 mb-1 font-medium">תוקף (חודשים מרגע הרכישה)</span>
    <input-number
      v-model="form.validityMonths"
      :min="1"
      :max="60"
      placeholder="למשל: 24"
    />

    <span class="mt-2 mb-1 font-medium">טקסט שיווקי (לא חובה)</span>
    <InputText
      v-model="form.marketingText"
      placeholder="למשל: שובר מתנה לאדם אהוב"
    />

    <settings-toggle
      class="mt-3"
      :status="form.active ?? true"
      :disabled="!hasPayments"
      title="פעיל באתר"
      :description="hasPayments ? '' : 'לא ניתן לפרסם באתר — לא חובר אמצעי תשלום בעסק'"
      @value-changed="(val) => form.active = val"
    />

    <Button
      v-if="isEdit"
      class="mt-auto"
      severity="danger"
      text
      label="הסרת השובר מהאתר"
      :loading="deleting"
      @click="remove"
    />

    <drawer-footer
      :class="{ 'mt-auto': !isEdit }"
      :approveDisable="!canSubmit"
      :approveLoading="loading"
      :approve-label="isEdit ? 'שמירת שינויים' : 'פרסום באתר'"
      @approve-click="submit"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, reactive, ref } from 'vue';
import { IService, IVoucherTemplate, VoucherTypes } from '@torim-app/types';
import SelectService from '@app/components/admin/components/SelectService.vue';
import { PromotionsApi } from '@app/api/admin/promotions-api';
import { useToast } from 'vue-toastification';
import { useAdminStore } from '@app/stores';
import { useConfirm } from 'primevue/useconfirm';

const props = defineProps({
  template: {
    type: Object as PropType<IVoucherTemplate | null>,
    required: false,
    default: null
  }
});

const emit = defineEmits(['close', 'created', 'updated', 'deleted']);
const toast = useToast();
const adminStore = useAdminStore();
const confirm = useConfirm();

const isEdit = computed(() => !!props.template?._id);

const form = reactive<Partial<IVoucherTemplate>>({
  voucherType: VoucherTypes.AMOUNT,
  amount: undefined,
  serviceIds: [],
  serviceDiscount: undefined,
  validityMonths: 24,
  marketingText: '',
  active: true
});

// A voucher can't be sold on the site without a connected payment provider, so "פעיל באתר"
// is forced off and locked (set in setup so the toggle initializes correctly).
const hasPayments = computed(() => !!adminStore.getBusinessSiteData?.hasPayments);
if (!hasPayments.value) form.active = false;

const selectedServices = ref<IService[]>([]);
const loading = ref(false);
const deleting = ref(false);

const onServicesSelected = (services: IService[]) => {
  selectedServices.value = services;
  form.serviceIds = services.map(s => s.id);
};

const serviceTotalPrice = computed(() =>
  selectedServices.value.reduce((sum, s) => sum + (s.price ?? 0), 0)
);

const canSubmit = computed(() => {
  if (form.voucherType === VoucherTypes.AMOUNT) {
    return !!form.amount && !!form.validityMonths;
  }
  return (form.serviceIds?.length ?? 0) > 0 && !!form.validityMonths;
});

const submit = async () => {
  if (!canSubmit.value) return;
  loading.value = true;
  try {
    if (isEdit.value && props.template?._id) {
      const updated = await PromotionsApi.updateVoucherTemplate(props.template._id, form);
      toast.success('נשמר');
      emit('updated', updated);
    } else {
      const created = await PromotionsApi.createVoucherTemplate(form);
      toast.success('שובר פורסם באתר');
      emit('created', created);
    }
    emit('close');
  } catch (e) {
    console.error(e);
    toast.error('שגיאה בשמירה');
  } finally {
    loading.value = false;
  }
};

const remove = () => {
  if (!props.template?._id) return;
  confirm.require({
    message: 'להסיר את השובר מהאתר?',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      deleting.value = true;
      try {
        await PromotionsApi.deleteVoucherTemplate(props.template!._id!);
        toast.success('השובר הוסר');
        emit('deleted', props.template!._id);
        emit('close');
      } catch (e) {
        toast.error('שגיאה');
      } finally {
        deleting.value = false;
      }
    }
  });
};

onMounted(() => {
  if (props.template) {
    Object.assign(form, props.template);
    if (props.template.serviceIds?.length) {
      const allServices = adminStore.businessSiteData?.services ?? [];
      selectedServices.value = allServices.filter(s => props.template!.serviceIds!.includes(s.id));
    }
    // Re-apply after seeding from the template: no payment provider → can't be on the site.
    if (!hasPayments.value) form.active = false;
  }
});
</script>
